import { useDispatch, useSelector } from "react-redux";
import CustomPage from "components/CustomPage";
import { useState } from "react";
import DatesReports from "components/CustomSearch";
import moment from "moment";
import NullCheck from "components/NullCheck";
import http from "../../../../http";
import { DATE_FOR_API_FORMAT, DATE_TIME_FORMAT, MC_DEVICE_TYPE, MC_WIHT_RELAY } from "config/const";
import { apiUrl } from "config/settings";
import { setShowLoading, setShowMassage } from "store/sysSlice";
import CustomDataGrid from "components/customDataGrid";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import CustomCard from "components/CustomCard";
import { getIconByDevice } from "containers/controlAndMonitoring/const";
import { findKeyByValue } from "config/functions";
import { CalendarMonth, FmdGoodOutlined } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";

const USER_NOTES = new Set(["LEGACY_SCHEDULE_ACTIVE", "CONTROLLER_OFFLINE", "EXPIRED", "ASSIGNMENT_CHANGED", "STATE_CHANGED", "DELIVERY_UNCERTAIN", "STATE_SYNC_FAILED", "MANUAL_OFF"]);

const McRelayPowerReport = () => {
  const dispatch = useDispatch();
  const { bt } = useBizTypeTranslation();
  const mcList = useSelector((state) => state?.mc.mcList);
  const [datesReports, setDatesReports] = useState(true);
  const [list, setList] = useState([]);
  const [data, setData] = useState(null);

  const callBackDate = async (data) => {
    if (!data?.selectedMc) {
      dispatch(setShowLoading(false));
      dispatch(setShowMassage({ massage: "SELECT_MC_ERROR", type: "error" }));
      setTimeout(() => setDatesReports(true), 100);
      return
    }
    if (data) {
      dispatch(setShowLoading(true));
      const dates = {
        start: moment(data?.startDate).startOf('day').format(DATE_FOR_API_FORMAT),
        end: moment(data?.endDate).endOf('day')?.format(DATE_FOR_API_FORMAT),
      };

      http
        .get(`${apiUrl}/reports/relayPower/${data?.selectedMc}/${dates?.start}/${dates?.end}`)
        .then((res) => {
          dispatch(setShowLoading(false));
          const reportItems = res?.data?.data?.filter((item) => item.type !== "SCHEDULE_SIMULATION");
          const resData = {
            dates: dates,
            list: reportItems,
            mcData: mcList?.find((item) => item?.guid === data?.selectedMc),
          }
          setList(reportItems);
          setData(resData);
        });
    }
  };

  return (
    <CustomPage hideTitle >
      <Stack spacing={2} mb={2}>

        <CustomCard width="100%" direction="row" pb={1} padding={1} alignItems="center" justifyContent="space-between">

          <Stack direction={{ xs: "column", md: "row" }} justifyContent='space-between'>
            <Stack spacing={1}>
              <Typography color="primary.text">
                {data?.mcData.name}
              </Typography>

              <Stack direction="row" spacing={{ xs: 1, md: 2 }}>
                <NullCheck valueForCheck={data?.mcData?.deviceType !== MC_DEVICE_TYPE.NONE}>
                  <Stack alignItems="center" direction="row" spacing={0.5}>
                    {getIconByDevice(data?.mcData?.deviceType)}
                    <Typography variant="body2" color="primary.text">
                      {bt(findKeyByValue(MC_DEVICE_TYPE, data?.mcData?.deviceType))}
                    </Typography>
                  </Stack>
                </NullCheck>

                <NullCheck valueForCheck={data?.mcData?.area?.name}>
                  <Stack alignItems="center" direction="row" spacing={0.5}>
                    <FmdGoodOutlined fontSize="small" sx={{ color: "primary.main" }} />
                    <Typography variant="body2" color="primary.text">
                      {data?.mcData?.area?.name}
                    </Typography>
                  </Stack>
                </NullCheck>
              </Stack>
            </Stack>
            <Stack />
          </Stack>

          <IconButton onClick={() => setDatesReports(true)}>
            <CalendarMonth />
          </IconButton>
        </CustomCard>

        <NullCheck valueForCheck={list?.length > 0} noData='NO_DATA'>
          <CustomCard spacing={0} padding={0}>
            <Stack padding={1} direction={{ xs: "column-reverse", md: "row" }} justifyContent="space-between" spacing={2}>

              <CustomCard width="100%" height="100%">
                <CustomDataGrid
                  pageSize={12}
                  getRowClassName={(params) => { return params?.row?.isAlert ? "hot" : "" }}
                  stickyLeftColumns={["employeeName"]}
                  columns={[
                    {
                      field: "employeeName",
                      headerName: bt("EMPLOYEE_NAME"),
                      width: 250,
                      renderCell: (value) => value?.row?.employeeName,
                    },
                    {
                      field: "status",
                      headerName: bt("ACTION"),
                      width: 250,
                      renderCell: (value) => value?.row?.status === "NONE" ? "—" : `${bt(value?.row?.status)}`,
                    },
                    {
                      field: "type",
                      headerName: bt("TYPE"),
                      width: 250,
                      renderCell: (value) => ({ SCHEDULE_SIMULATION: "הדמיית לוח זמנים", SCHEDULE_COMMAND: "פקודת לוח זמנים", BOOST: `בקשת בוסט (${value?.row?.durationMinutes} דקות)` }[value?.row?.type] || `${bt(value?.row?.type)}`),
                    },
                    ...(list.some((item) => USER_NOTES.has(item.errorCode)) ? [{
                      field: "errorCode", headerName: "הערה", width: 230,
                      renderCell: ({ row }) => USER_NOTES.has(row.errorCode) ? bt(`CS_REASON_${row.errorCode}`) : "—",
                    }] : []),
                    {
                      field: "created",
                      headerName: bt("CREATED"),
                      width: 250,
                      renderCell: (value) => `${bt(value?.row?.created)}`,
                    },
                  ]
                  }
                  list={list?.map((item) => ({
                    ...item,
                    employeeName: item?.employeeName || bt("SYSTEM"),
                    created: `${item?.created ? moment(item?.created).format(DATE_TIME_FORMAT) : ""}`,
                  }))}
                />
              </CustomCard>

            </Stack>
          </CustomCard>

        </NullCheck>
      </Stack>


      <DatesReports
        btnTitle="CREATE_REPORT"
        title='DATES'
        mcList={mcList
          ?.filter(item => MC_WIHT_RELAY.includes(item?.softwareType))
          ?.map((item) => ({ guid: item?.guid, name: item?.name }))
        }
        open={datesReports}
        setOpen={setDatesReports}
        callBack={callBackDate}
      />
    </CustomPage>
  );
};
// A business switch must discard the previous business's report and pending UI state.
const BusinessRelayPowerReport = () => {
  const businessId = useSelector((state) => state.biz?.data?.id);
  const role = useSelector((state) => state.userSettings?.user?.userRole);
  return businessId ? <McRelayPowerReport key={`${businessId}:${role}`} /> : null;
};
export default BusinessRelayPowerReport;
